import { Location, NgTemplateOutlet } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';
import { IconColor, IconModule, IconSize } from '@freelancer/ui/icon';
import {
  LinkColor,
  LinkHoverColor,
  LinkUnderline,
  QueryParams,
  QueryParamsHandling,
} from '@freelancer/ui/link';
import { LinkModule } from '@freelancer/ui/link/link.module';
import { TagColor, TagSize } from '@freelancer/ui/tag';
import { TagComponent } from '@freelancer/ui/tag/tag.component';
import type { Observable } from 'rxjs';
import { ReplaySubject, Subscription, combineLatest } from 'rxjs';
import { filter, map, shareReplay, startWith } from 'rxjs/operators';
import { Sidebar } from './sidebar.service';
import type { SidebarItemIconName } from './sidebar.types';
import type { SidebarSubItem } from './sidebar.types';

@Component({
  standalone: true,
  imports: [IconModule, LinkModule, NgTemplateOutlet, TagComponent],
  selector: 'fl-sidebar-item',
  template: `
    @if (clickable) {
      <fl-link
        class="SidebarItemHeader"
        [color]="LinkColor.FOREGROUND"
        [display]="'block'"
        [link]="link"
        [queryParams]="queryParams"
        [queryParamsHandling]="queryParamsHandling"
        [fragment]="fragment"
        [hoverColor]="LinkHoverColor.FOREGROUND"
        [underline]="LinkUnderline.NEVER"
        [attr.data-selected]="!children ? isSelected : null"
        [attr.data-clickable]="clickable"
        [attr.data-compact]="isCompact === false"
        (click)="toggleExpand($event)"
      >
        <ng-container *ngTemplateOutlet="headerContent"></ng-container>
      </fl-link>
    }

    @if (!clickable) {
      <div
        class="SidebarItemHeader"
        [attr.data-compact]="isCompact === false"
      >
        <ng-container *ngTemplateOutlet="headerContent"></ng-container>
      </div>
    }

    <ng-template #headerContent>
      <div
        class="SidebarItemHeader-inner"
        [attr.data-clickable]="clickable"
        [attr.data-edge-to-edge]="edgeToEdge"
      >
        @if (iconName) {
          <fl-icon
            class="SidebarItemHeader-icon mr-xsmall"
            [name]="iconName"
            [color]="iconColor"
            [useIconFont]="true"
            [fillIconFont]="iconFill"
          ></fl-icon>
        }
        @if (displayText) {
          <p
            class="SidebarItemHeader-title text-color-inherit font-weight-inherit {{
              label || children ? 'mr-xxsmall' : 'mr-none'
            }}"
          >
            {{ displayText }}
          </p>
        }
        <div class="SidebarItemHeader-support">
          @if (label) {
            <fl-tag
              class="SidebarItemHeader-label {{
                children ? 'mr-xxsmall' : 'mr-none'
              }}"
              [color]="TagColor.MID"
              [size]="TagSize.SMALL"
            >
              {{ label }}
            </fl-tag>
          }
          @if (children) {
            <fl-icon
              class="SidebarItemHeader-chevron"
              [name]="'ui-chevron-right'"
              [size]="IconSize.SMALL"
              [attr.data-expanded]="expanded"
              [useIconFont]="true"
              [fillIconFont]="true"
            ></fl-icon>
          }
        </div>
        <ng-content></ng-content>
      </div>
    </ng-template>
    <ng-template #displayTextTemplate>{{ displayText }}</ng-template>

    @if (children) {
      <div
        class="SidebarItemContent"
        [attr.data-expanded]="expanded"
        [attr.data-compact]="isCompact === false"
      >
        <div class="SidebarItemContent-inner">
          @for (
            item of children;
            track trackByLinkAndQueryParam($index, item);
            let isLast = $last
          ) {
            <div class="{{ !isLast ? 'mb-small' : 'mb-none' }}">
              <fl-link
                class="SidebarItemContent-link"
                [link]="item.link"
                [queryParams]="item.queryParams"
                [fragment]="item.fragment"
                [color]="item.color || LinkColor.FOREGROUND"
                [underline]="LinkUnderline.NEVER"
              >
                {{ item.displayText }}
              </fl-link>
            </div>
          }
        </div>
      </div>
    }
  `,
  styleUrls: ['./sidebar-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarItemComponent
  implements AfterViewInit, OnChanges, OnInit, OnDestroy
{
  private changeDetectorRef = inject(ChangeDetectorRef);
  private location = inject(Location);
  private router = inject(Router);
  private sidebar = inject(Sidebar);

  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LinkUnderline = LinkUnderline;
  TagColor = TagColor;
  TagSize = TagSize;

  @Input() link: string;
  @Input() queryParams?: QueryParams;
  @Input() queryParamsHandling?: QueryParamsHandling;
  @Input() fragment?: string;
  @Input() iconName: SidebarItemIconName;
  @Input() iconColor = IconColor.INHERIT;
  @Input() iconFill = false;
  @Input() displayText: string;
  @Input() children: readonly SidebarSubItem[];
  @Input() routeHasChildren?: boolean;
  @Input() label: string;
  @Input() selected: boolean;
  @Input() clickable = true;
  @Input() edgeToEdge = false;

  @ViewChild('displayTextTemplate')
  displayTextTemplate: TemplateRef<any>;

  isSelected = false;
  expanded = false;
  isCompact: boolean;
  currentUrl$: Observable<string>;

  private linkSubject$ = new ReplaySubject<string>(1);
  private subscriptions = new Subscription();

  ngOnChanges(): void {
    if (this.selected !== undefined) {
      this.isSelected = this.selected;
    }

    if (this.link) {
      this.linkSubject$.next(this.link);
    }
  }

  ngOnInit(): void {
    this.currentUrl$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.location.path()),
      startWith(this.location.path()),
      // we don't support query params and fragments so just ignore them in the router url
      map(url => url.split('?')[0].split('#')[0].split(';')[0]),
      // Decode special characters
      map(decodeURI),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.subscriptions.add(
      combineLatest([
        this.currentUrl$,
        this.linkSubject$.asObservable(),
      ]).subscribe(([currentUrl, _]) => {
        if (this.selected === undefined) {
          this.isSelected = this.routeMatch(currentUrl);
        }

        if (!this.displayTextTemplate) {
          return;
        }

        if (this.isSelected) {
          this.sidebar.setActiveTab(this.displayTextTemplate);
        }

        this.changeDetectorRef.markForCheck();
      }),
    );

    this.subscriptions.add(
      this.sidebar.isOpen().subscribe(isOpen => {
        // For viewport where the sidebar becomes compact when closed
        if (
          window.innerWidth >=
            parseInt(FreelancerBreakpointValues.DESKTOP_SMALL, 10) &&
          window.innerWidth <=
            parseInt(FreelancerBreakpointValues.DESKTOP_LARGE, 10)
        ) {
          if (!isOpen) {
            this.expanded = false;
          }

          this.isCompact = isOpen;
          this.changeDetectorRef.markForCheck();
        }
      }),
    );
  }

  ngAfterViewInit(): void {
    if (this.isSelected) {
      this.sidebar.setActiveTab(this.displayTextTemplate);
    }
  }

  routeMatch(currentUrl: string): boolean {
    if (!this.link) {
      return false;
    }

    return this.routeHasChildren
      ? currentUrl.startsWith(this.link)
      : currentUrl === this.link;
  }

  toggleExpand(event: MouseEvent): void {
    if (this.children) {
      event.preventDefault();
      this.expanded = !this.expanded;
      this.sidebar.toggle(true);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.sidebar.setDeactivedTab(this.displayTextTemplate);
  }

  trackByLinkAndQueryParam(_: number, item: SidebarSubItem): string {
    const queryParamString = Object.entries(item.queryParams ?? {})
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return `${item.link}?${queryParamString}`;
  }
}
